// frontend/src/contexts/AnalysisContext.js
import React, { createContext, useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';

const AnalysisContext = createContext();

const DEFAULT_PREFS = {
    default_content_type: 'image',
    displayed_content_types: ['image', 'text', 'video', 'audio'],
    default_tasks: [],
    displayed_tasks: [],
    default_regions: true,
    display_regions: true,
    default_explanation: true,
    display_explanation: true,
    default_qr_label: 'VERIFIED'
};

const AnalysisProvider = ({ children }) => {
    const { user } = useContext(AuthContext);

    const getPrefs = () => {
        if (user && user.analysis_preferences) {
            // Merge with DEFAULT_PREFS to ensure all keys exist
            return { ...DEFAULT_PREFS, ...user.analysis_preferences };
        }
        return DEFAULT_PREFS;
    };

    const initialPrefs = getPrefs();

    const [masterTaskOrder, setMasterTaskOrder] = useState([]);
    const [contentType, setContentType] = useState(initialPrefs.default_content_type);
    const [content, setContent] = useState('');
    const [tasks, setTasks] = useState(initialPrefs.default_tasks);
    const [contentPreviewUrl, setContentPreviewUrl] = useState(null);
    const [contentSourceUrl, setContentSourceUrl] = useState('');
    const [originalInputUrl, setOriginalInputUrl] = useState('');
    const [includeRegions, setIncludeRegions] = useState(initialPrefs.default_regions);
    const [includeExplanation, setIncludeExplanation] = useState(initialPrefs.default_explanation);
    
    const [analysisResults, setAnalysisResults] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [contextSourceUrl, setContextSourceUrl] = useState('');
    const [contextPlatform, setContextPlatform] = useState('');
    const [contextUserNotes, setContextUserNotes] = useState('');
    const [estimatedCostUnits, setEstimatedCostUnits] = useState(0);

    // --- Fetch Dynamic Task Defaults from Server ---
    // Add a ref to ensure we only ever fetch this configuration once per session
    const hasFetchedTaskConfig = useRef(false);
    useEffect(() => {
        const fetchTaskDefaults = async () => {
            try {
                const response = await fetch('/api/config/tasks');
                if (response.ok) {
                    const data = await response.json();
                    
                    // --- Save the exact top-to-bottom YAML order ---
                    const orderedKeys = Object.keys(data.tasks);
                    setMasterTaskOrder(orderedKeys);

                    const allActiveTasks = orderedKeys.filter(k => !data.tasks[k].is_meta_task);
                    const defaultUiTasks = allActiveTasks.filter(k => data.tasks[k].default_on_ui);

                    setTasks(prev => {
                        // Only auto-fill if the user has absolutely NO preferences saved (first login)
                        if (!prev || prev.length === 0) {
                            return defaultUiTasks;
                        }
                        return prev;
                    });
                }
            } catch (err) {
                console.error("Failed to fetch dynamic task defaults:", err);
                setTasks(prev => prev.length === 0 ? ['ai_detection', 'fake_news_detection'] : prev);
            } finally {
                // Mark as fetched so we don't accidentally do it again!
                hasFetchedTaskConfig.current = true;
            }
        };

        // ONLY run the fetch if we haven't done it yet
        if (!hasFetchedTaskConfig.current) {
            fetchTaskDefaults();
        }
        
    // CRITICAL: Remove `tasks.length` from this dependency array!
    }, []);
    
    // To auto-sort the selector dropdown
    useEffect(() => {
        // Automatically sort the user's selected tasks array to match the master YAML order
        if (masterTaskOrder.length > 0 && tasks.length > 0) {
            setTasks(prev => {
                const sorted = [...prev].sort((a, b) => {
                    const indexA = masterTaskOrder.indexOf(a);
                    const indexB = masterTaskOrder.indexOf(b);
                    // Push unknown keys to the end
                    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                });
                
                // Only update state if the order actually changed to prevent infinite loops
                if (JSON.stringify(sorted) !== JSON.stringify(prev)) {
                    return sorted;
                }
                return prev;
            });
        }
    }, [masterTaskOrder, tasks.length]); // Re-sort if master order loads or task count changes

    // --- Sync with User Preferences on Update ---
    const prevUserPrefsStr = useRef(JSON.stringify(initialPrefs));
    
    useEffect(() => {
        const currentPrefsStr = JSON.stringify(getPrefs());
        if (currentPrefsStr !== prevUserPrefsStr.current) {
            const prefs = getPrefs();
            setContentType(prefs.default_content_type);
            // Only override tasks if the user specifically has them saved in DB
            if (prefs.default_tasks && prefs.default_tasks.length > 0) {
                setTasks(prefs.default_tasks);
            }
            setIncludeRegions(prefs.default_regions);
            setIncludeExplanation(prefs.default_explanation);
            prevUserPrefsStr.current = currentPrefsStr;
        }
    // We intentionally depend ONLY on the user's prefs string to prevent excessive re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.analysis_preferences]);

    // This effect ensures that changing content type clears the old results.
    const prevContentTypeRef = useRef(contentType);
    useEffect(() => {
        if (prevContentTypeRef.current !== contentType) {
            setContent('');
            setAnalysisResults({});
            setUploadProgress(0);
            setContentPreviewUrl(null);
            setContentSourceUrl('');
            setContextSourceUrl('');
            setContextPlatform('');
            setContextUserNotes('');
        }
        prevContentTypeRef.current = contentType;
    }, [contentType]);


    const value = {
        contentType, setContentType,
        content, setContent,
        contentPreviewUrl, setContentPreviewUrl,
        contentSourceUrl, setContentSourceUrl,
        originalInputUrl, setOriginalInputUrl,
        tasks, setTasks,
        includeRegions, setIncludeRegions,
        includeExplanation, setIncludeExplanation,
        analysisResults, setAnalysisResults,
        isLoading, setIsLoading,
        isUploading, setIsUploading,
        error, setError,
        uploadProgress, setUploadProgress,
        contextSourceUrl, setContextSourceUrl,
        contextPlatform, setContextPlatform,
        contextUserNotes, setContextUserNotes,
        masterTaskOrder,
        estimatedCostUnits, setEstimatedCostUnits
    };

    return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
};

export { AnalysisContext, AnalysisProvider };